import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import ApiClient from '../../services/apiClient/ApiClient'
import {
  getTagsWithLandAssociations,
  getTagsWithBuildingAssociations,
} from '@/services/tagsService'
import type {
  TagWithUserInfo,
  TagWithLands,
  TagWithBuildings,
  TagsState,
  CreateTagDto,
} from '@popety-io/types'

const apiClient = new ApiClient()

// Async thunk for getting all tags
export const getAllTags = createAsyncThunk(
  'tags/getAllTags',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/tags/all')
      if (response.errors) {
        return rejectWithValue(response.errors[0])
      }
      return response.data as TagWithUserInfo[]
    } catch {
      return rejectWithValue({ message: 'Failed to fetch tags' })
    }
  }
)

// Async thunk for creating a tag
export const createTag = createAsyncThunk(
  'tags/createTag',
  async (tagData: CreateTagDto, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/tags', tagData)
      if (response.errors) {
        return rejectWithValue(response.errors[0])
      }
      return response.data as TagWithUserInfo
    } catch {
      return rejectWithValue({ message: 'Failed to create tag' })
    }
  }
)

// Async thunk for deleting a tag
export const deleteTag = createAsyncThunk(
  'tags/deleteTag',
  async (tagId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete(`/tags/${tagId}`)
      if (response.errors) {
        return rejectWithValue(response.errors[0])
      }
      return tagId
    } catch {
      return rejectWithValue({ message: 'Failed to delete tag' })
    }
  }
)

// Async thunk for getting tags with land associations
export const getTagsWithLands = createAsyncThunk(
  'tags/getTagsWithLands',
  async (_, { rejectWithValue }) => {
    try {
      const tagsWithLands = await getTagsWithLandAssociations()
      return tagsWithLands
    } catch (error: any) {
      return rejectWithValue({
        message: error.message || 'Failed to fetch tags with lands',
      })
    }
  }
)

// Async thunk for getting tags with building associations
export const getTagsWithBuildings = createAsyncThunk(
  'tags/getTagsWithBuildings',
  async (_, { rejectWithValue }) => {
    try {
      const tagsWithBuildings = await getTagsWithBuildingAssociations()
      return tagsWithBuildings
    } catch (error: any) {
      return rejectWithValue({
        message: error.message || 'Failed to fetch tags with buildings',
      })
    }
  }
)

const initialState: TagsState = {
  tags: [],
  tagsWithLands: [],
  tagsWithBuildings: [],
  loading: false,
  error: null,
}

const tagsSlice = createSlice({
  name: 'tags',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearTags: (state) => {
      state.tags = []
      state.tagsWithLands = []
      state.tagsWithBuildings = []
    },
  },
  extraReducers: (builder) => {
    builder
      // Get all tags
      .addCase(getAllTags.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        getAllTags.fulfilled,
        (state, action: PayloadAction<TagWithUserInfo[]>) => {
          state.loading = false
          state.tags = action.payload
        }
      )
      .addCase(getAllTags.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.message || 'Failed to fetch tags'
      })

      // Create tag
      .addCase(createTag.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        createTag.fulfilled,
        (state, action: PayloadAction<TagWithUserInfo>) => {
          state.loading = false
          state.tags.push(action.payload)
        }
      )
      .addCase(createTag.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.message || 'Failed to create tag'
      })

      // Delete tag
      .addCase(deleteTag.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteTag.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false
        state.tags = state.tags.filter((tag) => tag.id !== action.payload)
      })
      .addCase(deleteTag.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.message || 'Failed to delete tag'
      })

      // Get tags with lands
      .addCase(getTagsWithLands.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        getTagsWithLands.fulfilled,
        (state, action: PayloadAction<TagWithLands[]>) => {
          state.loading = false
          state.tagsWithLands = action.payload
        }
      )
      .addCase(getTagsWithLands.rejected, (state, action) => {
        state.loading = false
        state.error =
          action.payload?.message || 'Failed to fetch tags with lands'
      })

      // Get tags with buildings
      .addCase(getTagsWithBuildings.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        getTagsWithBuildings.fulfilled,
        (state, action: PayloadAction<TagWithBuildings[]>) => {
          state.loading = false
          state.tagsWithBuildings = action.payload
        }
      )
      .addCase(getTagsWithBuildings.rejected, (state, action) => {
        state.loading = false
        state.error =
          action.payload?.message || 'Failed to fetch tags with buildings'
      })
  },
})

export const { clearError, clearTags } = tagsSlice.actions

export const tagsReducer = { tags: tagsSlice.reducer }
